/**
 * MonthlyHeatmap — calendar grid of monthly returns.
 *
 * Props:
 *   data  [{year, month, return_pct}]  from analytics.monthly_returns
 */

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function cellStyle(v) {
  if (v == null) return { background: 'transparent' };
  const abs      = Math.abs(v);
  const opacity  = 0.12 + Math.min(abs / 8, 1) * 0.65;
  const bg       = v >= 0
    ? `rgba(52,211,153,${opacity.toFixed(2)})`
    : `rgba(248,113,113,${opacity.toFixed(2)})`;
  return { background: bg };
}

function cellText(v) {
  if (v == null) return { color: '#4b5563' };
  const abs = Math.abs(v);
  const dark = abs > 4;
  return { color: v >= 0 ? (dark ? '#064e3b' : '#059669') : (dark ? '#7f1d1d' : '#dc2626') };
}

function annualReturn(months) {
  if (!months.length) return null;
  const product = months.reduce((acc, m) => acc * (1 + m.return_pct / 100), 1);
  return parseFloat(((product - 1) * 100).toFixed(2));
}

export default function MonthlyHeatmap({ data = [] }) {
  if (!data.length) return null;

  // Build map: { year -> { month -> return_pct } }
  const byYear = {};
  for (const row of data) {
    if (!byYear[row.year]) byYear[row.year] = {};
    byYear[row.year][row.month] = row.return_pct;
  }
  const years = Object.keys(byYear).map(Number).sort();

  return (
    <div className="w-full overflow-x-auto">
      <table className="text-xs border-collapse w-full min-w-[640px]">
        <thead>
          <tr>
            <th className="text-left px-2 py-1.5 text-muted-foreground font-semibold w-12">Year</th>
            {MONTHS.map(m => (
              <th key={m} className="text-center px-1 py-1.5 text-muted-foreground font-medium">{m}</th>
            ))}
            <th className="text-center px-2 py-1.5 text-muted-foreground font-semibold">Annual</th>
          </tr>
        </thead>
        <tbody>
          {years.map(year => {
            const row      = byYear[year];
            const monthly  = MONTHS.map((_, i) => row[i + 1] ?? null);
            const ann      = annualReturn(monthly.filter(v => v != null).map((v, i) => ({ return_pct: v, month: i })));
            return (
              <tr key={year}>
                <td className="px-2 py-1 text-muted-foreground font-semibold">{year}</td>
                {monthly.map((v, i) => (
                  <td
                    key={i}
                    style={cellStyle(v)}
                    className="text-center px-1 py-1.5 border border-border font-medium tabular-nums transition-colors"
                  >
                    {v != null
                      ? <span style={cellText(v)}>{v > 0 ? '+' : ''}{v.toFixed(1)}</span>
                      : <span className="text-muted-foreground/30">·</span>
                    }
                  </td>
                ))}
                <td
                  style={cellStyle(ann)}
                  className="text-center px-2 py-1.5 border border-border font-bold tabular-nums"
                >
                  {ann != null
                    ? <span style={cellText(ann)}>{ann > 0 ? '+' : ''}{ann.toFixed(1)}%</span>
                    : <span className="text-muted-foreground/30">·</span>
                  }
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-[10px] text-muted-foreground mt-2">Monthly returns in %. Annual = compounded product of available months.</p>
    </div>
  );
}
